import { create, type StoreApi, type UseBoundStore } from 'zustand'
import { devtools, persist, type PersistOptions } from 'zustand/middleware'
import { produce } from 'immer'
import { useEffect, useMemo, useRef } from 'react'
import { isEqual } from 'lodash-es'
import pako from 'pako'
import { createUUID } from '@/utils'
import { isDev } from '@/env'
import { Buffer } from 'buffer'

/**
 * 移除 devtools，寫這麼久，還真用不到
 * 使用自訂的 debug 除錯
 */
type DebugMode = false | 'log' | 'trace'

// 目前都沒區分平台，使用同一個 namespace 就好
const NAMESPACE = 'PLATFORM'
// const NAMESPACE = String(process.env.REACT_APP_PLATFORM || 'NONE').toUpperCase()

/** 數據差異 */
export function referenceObjectXOR<T extends object>(obj1: T, obj2: T): Partial<T> {
  if (obj1 === obj2) return {} as Partial<T>
  const isObject = (val: any): val is object => val !== null && typeof val === 'object'
  if (!isObject(obj1) || !isObject(obj2)) {
    return obj2 as Partial<T>
  }
  const result: Partial<T> = {}
  const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)])
  for (const key of Array.from(keys)) {
    const k = key as keyof T // ⚡ 強制 key 為 keyof T
    const val1 = obj1[k]
    const val2 = obj2[k]
    if (isObject(val1) && isObject(val2)) {
      const d = referenceObjectXOR(val1, val2)
      if (Object.keys(d).length > 0) result[k] = d as T[keyof T]
    } else if (val1 !== val2) {
      result[k] = val2 as T[keyof T]
    }
  }
  return result
}

// 防記憶體改變暫時數據
export const object = Object.freeze({})
export const array = Object.freeze([])

// 清除非當前專案的暫存
if (typeof window !== 'undefined') {
  if (localStorage.getItem('namespace') !== NAMESPACE) {
    localStorage.clear()
  }
  localStorage.setItem('namespace', NAMESPACE)
}

type StoreReturn<T> = [
  // useStore
  UseBoundStore<StoreApi<T>>,
  // setState
  setState: (state: Partial<T> | ((state: T) => Partial<T> | void)) => void,
]

/** 登出回調佇列 */
export const logoutCallMap = new Map<string, UseBoundStore<StoreApi<any>>>()

/** 保存持久化名稱，登出自動刪除用 */
export const PersistNames: string[] = []
const addPersistName = (name: string) => {
  try {
    if (typeof window === 'undefined') return
    if (!PersistNames?.length) {
      const set = new Set(
        String(window.localStorage.getItem('PERSIST_NAMES') || '')
          .split(',')
          .filter(Boolean),
      )
      PersistNames.push(...Array.from(set))
    }
    if (PersistNames.includes(name)) return
    PersistNames.push(name)
    window.localStorage.setItem('PERSIST_NAMES', PersistNames.join(','))
  } catch {
    // ignore
  }
}

/** 綁定 state */
const bindState = <T = Record<string, any>>(initialState: T, name: string, debug: DebugMode = false) => {
  type Callback = (state: State) => Partial<State> | void
  type State = T & { update: (state: Partial<State> | Callback) => void }

  return (set: (partial: State | Partial<State> | ((state: State) => State | Partial<State>), replace?: false) => void, get: () => State) => {
    let update = (state: Partial<State> | Callback) => (typeof state === 'function' ? set(produce(state) as State) : set(state as State))
    if (debug) {
      update = (state: Partial<State> | Callback) => {
        const isFunction = typeof state === 'function'
        if (isFunction) {
          if (debug) {
            const prev = get()
            set(produce(state) as State)
            const next = get()
            console[debug!](`%cStore-${name}`, 'background:blue;color:white', referenceObjectXOR(prev, next))
          } else {
            set(produce(state) as State)
          }
        } else {
          if (debug) console[debug!](`%cStore-${name}`, 'background:blue;color:white', state)
          set(state)
        }
      }
    }
    /** 線上 */
    return {
      ...initialState,
      update,
    }
  }
}

/** 一般 */
const createBaseStore = <T = Record<string, any>>(initialState: T, name: string, debug: DebugMode = false): StoreReturn<T> => {
  type Callback = (state: State) => Partial<State> | void
  type State = T & { update: (state: Partial<State> | Callback) => void }
  const persistName = `${NAMESPACE}_${name}`
  const store = create<State>(bindState(initialState, persistName, debug))
  const setState = (state: Partial<State> | Callback) => store.getState().update(state)
  // 登出處理
  if (typeof (initialState as { logout?: () => T })?.['logout'] !== 'undefined') logoutCallMap.set(persistName, store)
  return [store as UseBoundStore<StoreApi<State>>, setState as (state: Partial<T> | ((state: T) => Partial<T> | void)) => void]
}

/** 持久化 */
const createPersistStore = <T = Record<string, any>>(initialState: T, options: PersistOptions<T>, debug: DebugMode = false): StoreReturn<T> => {
  type Callback = (state: State) => Partial<State> | void
  type State = T & { update: (state: Partial<State> | Callback) => void }
  const persistName = `${NAMESPACE}_${options.name}`
  addPersistName(persistName)
  const store = create(persist<State>(bindState(initialState, persistName, debug), { ...options, name: persistName } as PersistOptions<State>))
  // 登出處理
  if (typeof (initialState as { logout?: () => T })?.['logout'] !== 'undefined') logoutCallMap.set(options.name, store)
  const setState = (state: Partial<State> | Callback) => store.getState().update(state)
  return [store as UseBoundStore<StoreApi<State>>, setState as (state: Partial<T> | ((state: T) => Partial<T> | void)) => void]
}

/**
 * 生成 useStore
 * @test 有單元測試
 * @example
 *
 * // 登出處理
 * createStore({ logout: () => initialData() }, "DEMO"); // 返回一個登出後要覆蓋的數據，登出時自動呼叫
 *
 * // 一般 store + 四種用法
 * const [useDemo, setState] = createStore({ demo: 0 }, "DEMO");
 * setState({ demo: 1 });
 * setState((state) => void state.demo++);
 * setState((state) => ({ demo: state.demo + 1 }));
 * setState((state) => {
 *   state.demo = 0;
 * });
 *
 * // 持久化，且指定屬性存進 storage
 * const [useDemo, setState] = createStore(
 *   { demo: 0, demo2: 0 },
 *   {
 *     name: "DEMO",
 *     partialize: (state) => ({ demo2: state.demo2 }) as typeof state,
 *     // storage: createJSONStorage(() => sessionStorage) // 預設是 localStorage
 *   },
 * );
 */
export const defineStore = <T = Record<string, any>>(initialState: T & { logout?: (state: T) => Partial<T> }, options: string | PersistOptions<T>, debug: DebugMode = false) => {
  /** 一般 */
  if (typeof options === 'string') return createBaseStore<T>(initialState, options, debug)
  /** 持久化 */
  return createPersistStore<T>(initialState, options, debug)
}

/**
 * 等待 zustand store 數據
 * @test 有單元測試
 * @param {UseBoundStore<StoreApi<State>>} useStore defineStore 實體
 * @param {(state: State) => boolean} callback 等待條件，條件達到就返回所有 state 屬性
 * @example const { games } = await waitStore(useThirdGames, (state) => !!state?.games);
 */
export const waitStore = async <State>(useStore: UseBoundStore<StoreApi<State>>, callback: (state: State) => boolean): Promise<State> => {
  return new Promise((resolve) => {
    const state = useStore.getState()
    const condition = callback(state)
    if (condition) return resolve(state)
    const unSubscribe = useStore.subscribe((state: State) => {
      const condition = callback(state)
      if (!condition) return
      unSubscribe()
      return resolve(state)
    })
  })
}

/**
 * 淺比較
 * @test 有單元測試
 */
export const useShallow = <T = any>(params: T) => {
  const ref = useRef<T | undefined>(params)
  return useMemo(() => {
    ref.current = isEqual(ref.current, params) ? ref.current : params
    return ref.current
  }, [params])
}

/** 渲染次數檢測 */
export const useRenderCount = (deps: Parameters<typeof useEffect>[1]) => {
  const ref = useRef(0)
  useEffect(() => void ref.current++, deps)
  return ref
}

/** 確實需要全部 state 的情況才使用 */
export const getAll = <T>(state: T) => state

/** GZIP 版本的 storage */
export const createStorageGZIP = <State = any>(initialData: () => State) => {
  return () => ({
    getItem: (name: string): string => {
      try {
        const data = window.localStorage.getItem(name) || JSON.stringify(initialData)
        if (data.startsWith('{')) return data
        return pako.ungzip(Buffer.from(data, 'base64'), { to: 'string' })
      } catch {
        return JSON.stringify(initialData)
      }
    },
    setItem: (name: string, value: string): void => {
      try {
        window.localStorage.setItem(name, Buffer.from(pako.gzip(value)).toString('base64'))
      } catch {
        window.localStorage.setItem(name, value)
      }
    },
    removeItem: (name: string): void => {
      window?.localStorage?.removeItem(name)
    },
  })
}
